import { DATE_FORMAT_ISO, isValid, parse } from '@eneco/common/date';
import { capitalizeFirstLetter } from '@eneco/common/string';
import useDC from '@eneco/dc/useDC';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useFormatter } from '@eneco/i18n';
import { useGTM } from '@eneco/tracking';
import { Products_Orders_SwitchTypeResponse } from '@monorepo-types/dc';
import { useLayoutData } from '@sitecore/common';

import type {
  BaseEcommerce,
  DCOffer,
  EcommerceByEvent,
  EcommerceEventItem,
  FlowTrackingEvent,
  SimpleEcommerceEvent,
  SimpleEcommerceEventName,
} from './useFlowTracking.types';
import { FlowConsumerContextEnergy as FlowContext } from '../../context';
import { getUpsellProductPrice, getProductCategoryFromProductType } from '../helpers';
import { Offer, Product } from '../types';

const roundUsageValueToRange = (value: number, rangeSize: number): string => {
  // 1 >>> 1 - 100
  // 1500 >>> 1401 - 1500
  // 1501 >>> 1501 - 1600
  const min = Math.floor((value - 1) / rangeSize) * (value <= rangeSize ? 0 : rangeSize) + 1;
  const max = min + rangeSize - 1;

  return `${min} - ${max}`;
};

export const useFlowTracking = () => {
  const { label } = useDC();
  const { useFlowSelector } = useFlowHooks<FlowContext>();
  const flowState = useFlowSelector(state => state);
  const { pushToDataLayer } = useGTM();
  const { date } = useFormatter();

  const {
    context: flowContext,
    context: { isSME, voucherCode, basketId, hasUpsellElectricity, hasUpsellGas },
  } = flowState;

  const {
    route: { fields: { flowName } = {} },
  } = useLayoutData();

  /** Formats a YYYY-MM-DD contract start date for GTM (nl-NL medium → DD-MM-YYYY). */
  const formatContractStartDate = (contractStartDate?: string): string => {
    if (!contractStartDate || !isValid(contractStartDate)) return 'n/a';

    const parsedDate = parse(contractStartDate, DATE_FORMAT_ISO);
    if (!isValid(parsedDate)) return 'n/a';

    return date.medium(parsedDate);
  };
  const pushEvent = (event: FlowTrackingEvent, isEcommerce = false) => {
    if (isEcommerce) pushToDataLayer({ ecommerce: null });

    // JSON stringify && parse to filter out properties with undefined values
    return pushToDataLayer(JSON.parse(JSON.stringify(event)));
  };

  const mapOfferProductsToEventItems = (selectedProducts: Product[], dcOffer?: DCOffer): EcommerceEventItem[] =>
    selectedProducts.map((product, index) => ({
      item_name: product.description ?? 'n/a',
      item_variant: `${product.contractSpan?.duration}-${product.contractSpan?.byPeriod}`,
      item_id: product.code,
      product_combination: product.productCombinationCode ?? 'n/a',
      item_brand: capitalizeFirstLetter(label),
      item_list_name: flowName?.value?.toString() ?? 'n/a',
      price: Number(product.costsMonthly?.vatIncluded ?? 0),
      discount: 0,
      item_category: isSME ? 'zakelijk' : 'consumenten',
      item_category2: getProductCategoryFromProductType(product.type),
      item_category3: product.type,
      item_category4: 'n/a',
      customer_segment: dcOffer?.configuration?.actionCode ?? 'n/a',
      index,
      quantity: 1,
    }));

  const buildEcommercePayload = <TExtra extends object = object>(
    offer: Offer,
    dcOffer?: DCOffer,
    extra?: TExtra,
  ): BaseEcommerce & TExtra =>
    ({
      items: mapOfferProductsToEventItems(offer.products, dcOffer),
      nbaContext: offer.nbaContext,
      ...extra,
    }) as BaseEcommerce & TExtra;

  const pushEcommerceEvent = <TEvent extends SimpleEcommerceEventName>(
    event: TEvent,
    ecommerce: EcommerceByEvent[TEvent],
  ): void => {
    pushEvent({ event, ecommerce } as Extract<SimpleEcommerceEvent, { event: TEvent }>, true);
  };

  const trackViewItem = (offer: Offer, dcOffer?: DCOffer): void => {
    pushEcommerceEvent('view_item', buildEcommercePayload(offer, dcOffer));
  };

  const trackSelectItem = (offer: Offer, dcOffer?: DCOffer, preSelected?: boolean): void => {
    pushEcommerceEvent(
      'select_item',
      buildEcommercePayload(offer, dcOffer, { pre_selected: preSelected ? 'yes' : 'no' }),
    );
  };

  const trackViewCart = (_offer: Offer, _dcOffer?: DCOffer): void => {
    // TODO: uncomment it
    // temporary disabled "view_cart" event due to errors in the GA implementation
    // pushEcommerceEvent(
    //   'view_cart',
    //   buildEcommercePayload(offer, dcOffer, {
    //     value: Number(offer.costsMonthly?.vatIncluded),
    //     currency: 'EUR',
    //   }),
    // );
  };

  const trackAddToCart = (offer: Offer, dcOffer?: DCOffer): void => {
    pushEcommerceEvent('add_to_cart', buildEcommercePayload(offer, dcOffer));
  };

  const trackRemoveFromCart = (offer: Offer, dcOffer?: DCOffer): void => {
    pushEcommerceEvent('remove_from_cart', buildEcommercePayload(offer, dcOffer));
  };

  const trackAddToWishList = (offer: Offer, dcOffer?: DCOffer): void => {
    pushEcommerceEvent('add_to_wishlist', buildEcommercePayload(offer, dcOffer));
  };

  const trackBeginCheckout = (offer: Offer, dcOffer?: DCOffer): void => {
    pushEcommerceEvent('begin_checkout', buildEcommercePayload(offer, dcOffer));
  };

  const trackPurchase = (
    offer: Offer,
    dcOffer?: DCOffer,
    switchTypes?: Array<Products_Orders_SwitchTypeResponse>,
  ): void => {
    const { C1, C2, C3, phoneNumber, emailAddress } = flowContext;

    const items = mapOfferProductsToEventItems(offer.products, dcOffer).map(product => {
      const nextProduct = { ...product };

      if (hasUpsellGas && nextProduct.item_category3 === 'gas') {
        nextProduct.price_upsell_gas = Number(getUpsellProductPrice('gas', flowContext, dcOffer).toFixed(2));
      }

      if (hasUpsellElectricity && nextProduct.item_category3 === 'electricity') {
        nextProduct.price_upsell_electricity = Number(
          getUpsellProductPrice('electricity', flowContext, dcOffer).toFixed(2),
        );
      }

      nextProduct.item_category4 =
        switchTypes?.find(type => type.productType?.toLowerCase() === nextProduct.item_category3)?.orderType ?? '';

      return nextProduct;
    });

    pushEvent(
      {
        event: 'purchase',
        contract_startdate: formatContractStartDate(offer.contractStartDate),
        internalcampaign_id: '',
        internalcampaign_name: '',
        phonenumber: phoneNumber ?? 'n/a',
        email: emailAddress ?? 'n/a',
        optinc1: C1 ?? false,
        optinc2: C2 ?? false,
        optinc3: C3 ?? false,
        ecommerce: {
          transaction_id: basketId ?? '',
          value: Number(offer.costsMonthly?.vatIncluded ?? 0),
          tax: Number(offer.costsMonthly?.vatAmount ?? 0),
          currency: 'EUR',
          coupon: voucherCode ?? '',
          nbaContext: offer.nbaContext,
          items,
        },
      },
      true,
    );
  };

  const trackFillUsage = (context: FlowContext): void => {
    const {
      hasDoubleMeter,
      hasSolarPanels,
      usageElectricityHigh,
      usageElectricityLow,
      solarPanelsOutput,
      usageGas,
      usageWarmth,
      usageWater,
    } = context;

    pushEvent({
      event: 'fill_usage',
      usage_filltype: context.isUsageKnown ? 'manual' : 'estimated',
      ...(usageElectricityHigh && {
        usage_electricitypeak: roundUsageValueToRange(usageElectricityHigh, 100),
      }),
      ...(hasDoubleMeter &&
        usageElectricityLow && {
          usage_electricityoffpeak: roundUsageValueToRange(usageElectricityLow, 100),
        }),
      ...(hasSolarPanels &&
        solarPanelsOutput && {
          usage_redelivery: roundUsageValueToRange(solarPanelsOutput, 100),
        }),
      ...(usageGas && { usage_gas: roundUsageValueToRange(usageGas, 100) }),
      // usage_heating && usage_hottapwater must have smaller ranges due to smaller usage values
      ...(usageWarmth && { usage_heating: roundUsageValueToRange(usageWarmth, 10) }),
      ...(usageWater && { usage_hottapwater: roundUsageValueToRange(usageWater, 10) }),
      component_type: flowName?.value?.toString() ?? '',
    });
  };

  return {
    trackViewItem,
    trackSelectItem,
    trackViewCart,
    trackAddToCart,
    trackRemoveFromCart,
    trackAddToWishList,
    trackBeginCheckout,
    trackPurchase,
    trackFillUsage,
  };
};
